import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';
import { BOOST_STATUS_POLL_MS, BOOST_STATUS_POLL_TRIES, newIdempotencyKey } from './useBoostPayment.js';

/** Codes that send a paid request to Stripe Checkout instead (ENG-712/714). */
const CHECKOUT_FALLBACK = new Set(['saved_card_required', 'card_authentication_required']);

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Sends a request from the song page's check step (mockups 23, 24 and 27).
 *
 * - `free(song, requestedBy)` is a free request (`POST /queue`, ENG-720).
 * - `pay(song, amountDollars, { requestedBy, card })` is a paid one: with a
 *   saved card (`card.hasSavedCard`) one tap charges it
 *   (`POST /bands/{id}/queue/paid-requests`, ENG-712); without one, or when
 *   the bank wants the listener to confirm, Stripe Checkout takes the card
 *   (`/paid-requests/checkout`, ENG-714) and comes back here with
 *   `?request=success&tip=<id>`, after which the payment is read until it
 *   settles. `saveCard` asks Checkout to keep the card.
 *
 * One key per attempt, and nothing while one is under way, so a double click
 * sends one request. `onRequested({ entryId, spot, amountCents, free })`
 * follows a request in the queue. `state` is idle, sending, redirecting or
 * failed (with `error`, a stable code or the queue's own words).
 */
export const useRequestPayment = (bandId, { onRequested, returnPath } = {}) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const onRequestedRef = useRef(onRequested);
  useEffect(() => { onRequestedRef.current = onRequested; }, [onRequested]);
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const busyRef = useRef(false);
  const pendingRef = useRef(null);

  const done = useCallback((result) => {
    busyRef.current = false;
    pendingRef.current = null;
    setState('idle');
    setError(null);
    onRequestedRef.current?.(result);
  }, []);

  const fail = useCallback((code) => {
    busyRef.current = false;
    pendingRef.current = null;
    setState('failed');
    setError(code);
  }, []);

  const settle = useCallback((outcome, amountCents) => {
    if (outcome?.status === 'confirmed') {
      done({ entryId: outcome.entry_id ?? null, spot: outcome.spot ?? null, amountCents: outcome.amount_cents ?? amountCents, free: false });
      return true;
    }
    if (outcome?.status === 'refunded') {
      fail('request_refunded');
      return true;
    }
    if (outcome?.status === 'failed') {
      fail(outcome.failure_code || 'card_declined');
      return true;
    }
    return false;
  }, [done, fail]);

  const follow = useCallback(async (tipId, amountCents) => {
    pendingRef.current = tipId;
    for (let i = 0; i < BOOST_STATUS_POLL_TRIES; i += 1) {
      if (pendingRef.current !== tipId) return;
      try {
        const outcome = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandId}/queue/payments/${tipId}`);
        if (outcome?.status && outcome.status !== 'pending' && settle(outcome, amountCents)) return;
      } catch (_err) {
        // Try again.
      }
      await wait(BOOST_STATUS_POLL_MS);
    }
    if (pendingRef.current === tipId) fail('request_pending');
  }, [bandId, settle, fail]);

  const checkout = useCallback(async (song, amountCents, requestedBy, saveCard) => {
    const answer = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/queue/paid-requests/checkout`, {
      song_id: song.id,
      amount_cents: amountCents,
      requested_by: requestedBy,
      save_card: Boolean(saveCard),
      return_path: returnPath,
      idempotency_key: newIdempotencyKey(),
    });
    setState('redirecting');
    window.location.assign(answer.checkout_url);
  }, [bandId, returnPath]);

  const errorOf = (err, fallback) => err?.code || (typeof err?.message === 'string' && err?.status && err.message !== err.statusText ? err.message : fallback);

  const pay = useCallback(async (song, amountDollars, { requestedBy = '', card = null, saveCard = false } = {}) => {
    if (!song || busyRef.current) return;
    busyRef.current = true;
    const amountCents = Math.round(amountDollars * 100);
    setState('sending');
    setError(null);
    try {
      if (!card?.hasSavedCard) {
        await checkout(song, amountCents, requestedBy, saveCard);
        return;
      }
      try {
        const outcome = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/queue/paid-requests`, {
          song_id: song.id,
          amount_cents: amountCents,
          requested_by: requestedBy,
          idempotency_key: newIdempotencyKey(),
        });
        if (outcome?.status === 'pending') {
          await follow(outcome.tip_id, amountCents);
          return;
        }
        if (!settle(outcome, amountCents)) fail('request_failed');
      } catch (err) {
        if (CHECKOUT_FALLBACK.has(err?.code)) {
          await checkout(song, amountCents, requestedBy, saveCard);
          return;
        }
        throw err;
      }
    } catch (err) {
      fail(errorOf(err, 'request_failed'));
    }
  }, [bandId, checkout, follow, settle, fail]);

  const free = useCallback(async (song, requestedBy = '') => {
    if (!song || busyRef.current) return;
    busyRef.current = true;
    setState('sending');
    setError(null);
    try {
      const answer = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/queue`, {
        band_id: Number(bandId),
        title: song.title,
        artist: song.artist,
        requested_by: requestedBy,
      });
      done({ entryId: answer?.ID ?? answer?.id ?? null, spot: null, amountCents: 0, free: true });
    } catch (err) {
      fail(errorOf(err, 'request_failed'));
    }
  }, [bandId, done, fail]);

  // Back from Checkout: ?request=success&tip=<id> or ?request=cancelled.
  useEffect(() => {
    if (!bandId || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const result = url.searchParams.get('request');
    if (!result) return;
    const tipId = Number(url.searchParams.get('tip'));
    url.searchParams.delete('request');
    url.searchParams.delete('tip');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    if (result === 'success' && tipId > 0) {
      busyRef.current = true;
      setState('sending');
      follow(tipId);
    }
  }, [bandId, follow]);

  const reset = useCallback(() => {
    busyRef.current = false;
    pendingRef.current = null;
    setState('idle');
    setError(null);
  }, []);

  return { pay, free, state, error, reset };
};
